/**
 * Commissioning emails (/sow/emails, admin only).
 *
 * Settings (alert list, sender), a dropdown to pick one of the emails
 * the commissioning routine sends, then the wording on the left and a
 * live preview with sample data on the right.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import { SettingsPanel } from "../components/sowemails/SettingsPanel";
import { EmailPicker } from "../components/sowemails/EmailPicker";
import { EmailEditor } from "../components/sowemails/EmailEditor";
import { EmailPreview } from "../components/sowemails/EmailPreview";

const API = process.env.REACT_APP_BACKEND_URL;

export const apiError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function SowEmails() {
  const [emails, setEmails] = useState([]);
  const [roles, setRoles] = useState({});
  const [sender, setSender] = useState(null);
  const [alertList, setAlertList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((data) => {
    if (data.emails) setEmails(data.emails);
    if (data.roles) setRoles(data.roles);
    if (data.sender) setSender(data.sender);
    if (data.alert_list) setAlertList(data.alert_list);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sow-emails`);
      apply(data);
      setSelected((cur) => cur || data.emails?.[0]?.kind || null);
    } catch (e) {
      toast.error(apiError(e, "Could not load the emails"));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  const replace = (entry) => setEmails((list) => list.map((x) => (x.kind === entry.kind ? entry : x)));
  const current = emails.find((x) => x.kind === selected) || null;

  return (
    <StaffShell max="wide">
      <div className="flex flex-col gap-6" data-testid="sow-emails-page">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B4F8A] tracking-tight flex items-center gap-2">
            <Mail className="w-6 h-6" /> Commissioning emails
          </h1>
          <p className="text-sm text-stone-500 mt-1 max-w-3xl">
            The emails the commissioning routine sends, shown with the fictional Northwind project. Nothing here emails a client.
            See also the <Link to="/sow/preview" className="underline underline-offset-2 hover:text-stone-800" data-testid="sow-emails-preview-link">client flow preview</Link>.
          </p>
        </div>
        <SettingsPanel sender={sender} alertList={alertList} onSaved={apply} />
        {loading ? (
          <p className="text-sm text-stone-500" data-testid="sow-emails-loading">Loading emails…</p>
        ) : (
          <>
            <EmailPicker emails={emails} selected={selected} onSelect={(k) => { setSelected(k); setPreview(null); }} />
            {current && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                <EmailEditor key={current.kind} entry={current} roles={roles} alertList={alertList?.emails} onSaved={replace} onPreview={setPreview} />
                <div className="xl:sticky xl:top-20">
                  <EmailPreview preview={preview || current.preview} roles={roles} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </StaffShell>
  );
}
